/**
 * Golden Ball eligibility for a tournament match (specs/champions-league AC-4.5).
 *
 * Mirrors the server's `goldenBallRound.helper` exactly, but reads the competition's
 * server-sent `rules` rather than the World Cup frontend constants
 * (`GOLDEN_BALL_ELIGIBLE_FRONTEND_STAGES`, `FRONTEND_TO_GOLDEN_BALL_ROUND`), which encode a
 * fixed three-round group stage and cannot express an eight-matchday league phase.
 *
 * Pure — no React, no network.
 */

import type { CompetitionRulesSummary } from '../types/competitions.types';

/** Stages that are not multi-round park their single boost here. Matches the server. */
export const SINGLE_ROUND_SENTINEL = '1';

export interface GoldenBallMatchLike {
  stage: string;
  round: string | null;
  status: 'upcoming' | 'live' | 'finished';
  isPickEditable?: boolean;
}

export interface GoldenBallSlot {
  /** True when this competition allows a x2 anywhere in this match's stage. */
  eligible: boolean;
  /** The (stage, round) slot this match's boost occupies, or null when unresolvable. */
  round: string | null;
  /** False once the match can no longer take a boost (kicked off or finished). */
  placeable: boolean;
}

const NOT_ELIGIBLE: GoldenBallSlot = { eligible: false, round: null, placeable: false };

/**
 * The key a boost occupies: one per (stage, round). Lives here rather than in the hook so the
 * hook, its tests and any future consumer name a slot identically.
 */
export const slotKey = (stage: string, round: string): string => `${stage}#${round}`;

/**
 * Resolve whether a x2 may be placed on this match, and which slot it would occupy.
 *
 * @param match  the match, carrying the server's own `stage` and `round`
 * @param rules  the competition's server-sent rules; absent means no boost is offered
 */
export function resolveGoldenBallSlot(
  match: GoldenBallMatchLike | null | undefined,
  rules: CompetitionRulesSummary | null | undefined,
): GoldenBallSlot {
  if (!match || !rules) return NOT_ELIGIBLE;
  if (!rules.goldenBallStages?.includes(match.stage)) return NOT_ELIGIBLE;

  const validRounds = rules.multiRoundStages?.[match.stage];
  const isMultiRound = Array.isArray(validRounds) && validRounds.length > 0;

  // A multi-round stage takes the match's own round; every other stage uses the sentinel.
  // A multi-round match whose round is not in the configured set has no resolvable slot —
  // the server would reject it, so the control must not look tappable.
  const round = isMultiRound
    ? (validRounds.includes(String(match.round ?? '')) ? String(match.round) : null)
    : SINGLE_ROUND_SENTINEL;

  if (round == null) return { eligible: true, round: null, placeable: false };

  const placeable = match.status === 'upcoming' && (match.isPickEditable ?? true);
  return { eligible: true, round, placeable };
}
